import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import BarChart, { type BarDataPoint } from '../../../components/crm/charts/BarChart';
import LineChart, { type DataPoint as LineChartDataPoint } from '../../../components/crm/charts/LineChart';
import { pmMembersService, buildMemberMap, getDisplayName, type ProjectManagementMember } from '../../../services/projectManagement/membersService';
import { portfolioService, type PortfolioReport } from '../../../services/projectManagement/portfolioService';
import { projectsService, type Project } from '../../../services/projectManagement/projectsService';
import { tasksService, type Task } from '../../../services/projectManagement/tasksService';
import PmSavedViewsControl from './PmSavedViewsControl';

interface ReportsDashboardProps {
  organizationId: string;
}

const ReportsDashboard: React.FC<ReportsDashboardProps> = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('all');
  const [members, setMembers] = useState<ProjectManagementMember[]>([]);
  const [portfolio, setPortfolio] = useState<PortfolioReport | null>(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectsRes, tasksRes] = await Promise.all([
        projectsService.list({ organization_id: organizationId }),
        tasksService.list({ organization_id: organizationId }),
      ]);

      if (projectsRes.error || tasksRes.error) {
        toast.error('Failed to load reports data');
        return;
      }

      setProjects(projectsRes.data || []);
      setTasks(tasksRes.data || []);
    } catch (error) {
      toast.error('Failed to load reports data');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const fetchPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const { data, error } = await portfolioService.getPortfolio(organizationId);
      if (error) {
        setPortfolio(null);
      } else {
        setPortfolio(data);
      }
    } catch {
      setPortfolio(null);
    } finally {
      setPortfolioLoading(false);
    }
  }, [organizationId]);

  const fetchMembers = useCallback(async () => {
    try {
      const { data, error } = await pmMembersService.list(organizationId);
      if (error) {
        setMembers([]);
      } else {
        setMembers(data || []);
      }
    } catch {
      setMembers([]);
    }
  }, [organizationId]);

  useEffect(() => {
    void fetchData();
    void fetchPortfolio();
    void fetchMembers();
  }, [fetchData, fetchPortfolio, fetchMembers]);

  // Calculate metrics
  const memberMap = React.useMemo(() => buildMemberMap(members), [members]);

  const visibleProjects = React.useMemo(() => {
    if (selectedProjectId === 'all') return projects;
    return projects.filter((p) => p.id === selectedProjectId);
  }, [projects, selectedProjectId]);

  const visibleTasks = React.useMemo(() => {
    if (selectedProjectId === 'all') return tasks;
    return tasks.filter((t) => t.project_id === selectedProjectId);
  }, [tasks, selectedProjectId]);

  const metrics = React.useMemo(() => {
    const totalProjects = visibleProjects.length;
    const activeProjects = visibleProjects.filter(p => p.status === 'active').length;
    const completedProjects = visibleProjects.filter(p => p.status === 'completed').length;
    
    const totalTasks = visibleTasks.length;
    const completedTasks = visibleTasks.filter(t => t.status === 'done').length;
    const inProgressTasks = visibleTasks.filter(t => t.status === 'in-progress').length;
    const blockedTasks = visibleTasks.filter(t => t.status === 'blocked').length;
    
    const overdueTasks = visibleTasks.filter(t => 
      t.due_date && new Date(t.due_date) < new Date() && t.status !== 'done'
    ).length;
    
    const totalEstimatedHours = visibleTasks.reduce((sum, t) => sum + (t.estimated_time || 0), 0);
    const totalActualHours = visibleTasks.reduce((sum, t) => sum + (t.actual_time || 0), 0);
    
    const completionRate = totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0;
    const avgProjectCompletion = visibleProjects.length > 0
      ? visibleProjects.reduce((sum, p) => sum + p.completion_percentage, 0) / visibleProjects.length
      : 0;
    
    const tasksByPriority = {
      urgent: visibleTasks.filter(t => t.priority === 'urgent').length,
      high: visibleTasks.filter(t => t.priority === 'high').length,
      medium: visibleTasks.filter(t => t.priority === 'medium').length,
      low: visibleTasks.filter(t => t.priority === 'low').length,
    };

    const tasksByStatus = {
      backlog: visibleTasks.filter(t => t.status === 'backlog').length,
      todo: visibleTasks.filter(t => t.status === 'todo').length,
      'in-progress': inProgressTasks,
      review: visibleTasks.filter(t => t.status === 'review').length,
      done: completedTasks,
      blocked: blockedTasks,
    };

    return {
      totalProjects,
      activeProjects,
      completedProjects,
      totalTasks,
      completedTasks,
      inProgressTasks,
      blockedTasks,
      overdueTasks,
      totalEstimatedHours,
      totalActualHours,
      completionRate,
      avgProjectCompletion,
      tasksByPriority,
      tasksByStatus,
    };
  }, [visibleProjects, visibleTasks]);

  const workloadByAssignee = React.useMemo<BarDataPoint[]>(() => {
    const counts = new Map<string, number>();

    visibleTasks.forEach((t) => {
      if (t.status === 'done') return;
      const key = t.assignee_id || '';
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    const data: BarDataPoint[] = Array.from(counts.entries()).map(([assigneeId, count]) => {
      if (!assigneeId) return { label: 'Unassigned', value: count };
      const member = memberMap[assigneeId];
      const label = member ? getDisplayName(member) : `User ${assigneeId.slice(0, 6)}`;
      return { label, value: count };
    });

    data.sort((a, b) => b.value - a.value);
    return data.slice(0, 10);
  }, [visibleTasks, memberMap]);

  const throughputData = React.useMemo<LineChartDataPoint[]>(() => {
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const startOfDay = (d: Date) => {
      const x = new Date(d.getTime());
      x.setHours(0, 0, 0, 0);
      return x;
    };
    const formatYmd = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const addDays = (d: Date, days: number) => {
      const x = new Date(d.getTime());
      x.setDate(x.getDate() + days);
      return x;
    };
    const startOfWeek = (d: Date) => {
      const x = startOfDay(d);
      const day = x.getDay();
      const diff = (day + 6) % 7;
      x.setDate(x.getDate() - diff);
      return x;
    };
    const startOfMonth = (d: Date) => {
      const x = startOfDay(d);
      x.setDate(1);
      return x;
    };

    const now = new Date();
    const end = startOfDay(now);
    let start = new Date(end.getTime());
    if (selectedPeriod === 'week') start.setDate(start.getDate() - 6);
    if (selectedPeriod === 'month') start.setDate(start.getDate() - 29);
    if (selectedPeriod === 'quarter') start.setDate(start.getDate() - 89);
    if (selectedPeriod === 'year') start.setDate(start.getDate() - 364);

    const bucket: 'day' | 'week' | 'month' =
      selectedPeriod === 'quarter' ? 'week' : selectedPeriod === 'year' ? 'month' : 'day';

    const counts = new Map<string, number>();
    visibleTasks.forEach((t) => {
      if (t.status !== 'done') return;
      if (!t.completed_at) return;
      const completed = new Date(t.completed_at);
      if (Number.isNaN(completed.getTime())) return;
      if (completed < start || completed > now) return;

      let key = '';
      if (bucket === 'day') key = formatYmd(completed);
      if (bucket === 'week') key = formatYmd(startOfWeek(completed));
      if (bucket === 'month') key = `${completed.getFullYear()}-${pad2(completed.getMonth() + 1)}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });

    const points: LineChartDataPoint[] = [];

    if (bucket === 'day') {
      for (let d = startOfDay(start); d.getTime() <= end.getTime(); d = addDays(d, 1)) {
        const key = formatYmd(d);
        points.push({ label: `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`, value: counts.get(key) || 0 });
      }
      return points;
    }

    if (bucket === 'week') {
      for (let d = startOfWeek(start); d.getTime() <= end.getTime(); d = addDays(d, 7)) {
        const key = formatYmd(d);
        points.push({ label: `${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`, value: counts.get(key) || 0 });
      }
      return points;
    }

    for (
      let d = startOfMonth(start);
      d.getTime() <= end.getTime();
      d = new Date(d.getFullYear(), d.getMonth() + 1, 1)
    ) {
      const key = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
      points.push({ label: d.toLocaleString(undefined, { month: 'short' }), value: counts.get(key) || 0 });
    }
    return points;
  }, [visibleTasks, selectedPeriod]);

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading reports...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Reports & Analytics</h2>
          <p className="text-sm text-gray-600 mt-1">
            Performance insights and metrics
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <PmSavedViewsControl
            organizationId={organizationId}
            scope="reports"
            getCurrentPayload={() => ({
              selectedProjectId,
              selectedPeriod,
            })}
            applyPayload={(payload) => {
              const nextProjectId = String((payload as any)?.selectedProjectId || 'all');
              const nextPeriod = String((payload as any)?.selectedPeriod || '');
              setSelectedProjectId(nextProjectId || 'all');
              if (nextPeriod === 'week' || nextPeriod === 'month' || nextPeriod === 'quarter' || nextPeriod === 'year') {
                setSelectedPeriod(nextPeriod);
              }
            }}
          />

          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            <option value="all">All Projects</option>
            {projects
              .slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>

          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm"
          >
            <option value="week">Last 7 Days</option>
            <option value="month">Last 30 Days</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>

          <button
            onClick={() => void fetchPortfolio()}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            Reassess
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Portfolio Health</h3>
            <p className="mt-1 text-sm text-gray-600">At-risk projects and organization-level rollups</p>
          </div>
          <button
            onClick={() => void fetchPortfolio()}
            className="rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>

        {portfolioLoading ? (
          <div className="text-sm text-gray-500">Loading portfolio...</div>
        ) : !portfolio ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center text-sm text-gray-600">
            Portfolio data not available
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <div className="space-y-3">
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Projects at risk</div>
                  <div className="mt-1 text-2xl font-bold text-gray-900">{portfolio.stats.projects_at_risk}</div>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Overdue tasks</div>
                  <div className="mt-1 text-2xl font-bold text-gray-900">{portfolio.stats.overdue_tasks}</div>
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">Blocked tasks</div>
                  <div className="mt-1 text-2xl font-bold text-gray-900">{portfolio.stats.blocked_tasks}</div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="rounded-lg border border-gray-200 p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-900">At-risk projects</div>
                  <div className="text-xs text-gray-500">Top issues: overdue + blocked</div>
                </div>

                {portfolio.at_risk_projects.length === 0 ? (
                  <div className="text-sm text-gray-600">No at-risk projects</div>
                ) : (
                  <div className="space-y-2">
                    {portfolio.at_risk_projects.slice(0, 8).map((p) => (
                      <div key={p.id} className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-gray-900">{p.name}</div>
                          <div className="mt-1 text-xs text-gray-600">{p.completed_tasks}/{p.total_tasks} done • {p.completion_percentage}%</div>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="rounded-full bg-red-100 px-2 py-1 font-semibold text-red-700">⚠️ {p.overdue_tasks}</span>
                          <span className="rounded-full bg-yellow-100 px-2 py-1 font-semibold text-yellow-800">⛔ {p.blocked_tasks}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Key Metrics Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Total Projects</span>
            <span className="text-2xl">📁</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-900">{metrics.totalProjects}</span>
            <span className="text-sm text-green-600">{metrics.activeProjects} active</span>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Total Tasks</span>
            <span className="text-2xl">✓</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-900">{metrics.totalTasks}</span>
            <span className="text-sm text-blue-600">{metrics.inProgressTasks} in progress</span>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Completion Rate</span>
            <span className="text-2xl">🎯</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-900">{metrics.completionRate.toFixed(1)}%</span>
            <span className="text-sm text-gray-600">{metrics.completedTasks} done</span>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600">Overdue Tasks</span>
            <span className="text-2xl">⚠️</span>
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className={`text-3xl font-bold ${metrics.overdueTasks > 0 ? 'text-red-600' : 'text-green-600'}`}>
              {metrics.overdueTasks}
            </span>
            {metrics.overdueTasks > 0 && (
              <span className="text-sm text-red-600">needs attention</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Workload by Assignee</h3>
          <BarChart data={workloadByAssignee} height={320} horizontal colorScheme="multi" />
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Throughput</h3>
          <LineChart data={throughputData} height={320} showLabels={selectedPeriod === 'week'} />
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Tasks by Status */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tasks by Status</h3>
          <div className="space-y-3">
            {Object.entries(metrics.tasksByStatus).map(([status, count]) => {
              const percentage = metrics.totalTasks > 0 ? (count / metrics.totalTasks) * 100 : 0;
              const colors: Record<string, string> = {
                backlog: 'bg-gray-500',
                todo: 'bg-blue-500',
                'in-progress': 'bg-purple-500',
                review: 'bg-yellow-500',
                done: 'bg-green-500',
                blocked: 'bg-red-500',
              };

              return (
                <div key={status}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700 capitalize">{status}</span>
                    <span className="text-gray-600">{count} ({percentage.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full ${colors[status]} transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tasks by Priority */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Tasks by Priority</h3>
          <div className="space-y-3">
            {Object.entries(metrics.tasksByPriority).map(([priority, count]) => {
              const percentage = metrics.totalTasks > 0 ? (count / metrics.totalTasks) * 100 : 0;
              const colors: Record<string, string> = {
                urgent: 'bg-red-500',
                high: 'bg-orange-500',
                medium: 'bg-yellow-500',
                low: 'bg-green-500',
              };

              return (
                <div key={priority}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700 capitalize">{priority}</span>
                    <span className="text-gray-600">{count} ({percentage.toFixed(0)}%)</span>
                  </div>
                  <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div
                      className={`h-full ${colors[priority]} transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Time Tracking */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Time Tracking</h3>
        <div className="grid gap-6 md:grid-cols-3">
          <div>
            <span className="text-sm text-gray-600">Estimated Hours</span>
            <div className="mt-1 text-2xl font-bold text-gray-900">{metrics.totalEstimatedHours}h</div>
          </div>
          <div>
            <span className="text-sm text-gray-600">Actual Hours</span>
            <div className="mt-1 text-2xl font-bold text-gray-900">{metrics.totalActualHours}h</div>
          </div>
          <div>
            <span className="text-sm text-gray-600">Time Variance</span>
            <div className={`mt-1 text-2xl font-bold ${
              metrics.totalActualHours > metrics.totalEstimatedHours ? 'text-red-600' : 'text-green-600'
            }`}>
              {metrics.totalEstimatedHours > 0 
                ? `${((metrics.totalActualHours / metrics.totalEstimatedHours - 1) * 100).toFixed(1)}%`
                : '0%'
              }
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Progress</span>
            <span className="text-gray-600">
              {metrics.totalActualHours}h / {metrics.totalEstimatedHours}h
            </span>
          </div>
          <div className="h-3 w-full overflow-hidden rounded-full bg-gray-200">
            <div
              className={`h-full transition-all ${
                metrics.totalActualHours > metrics.totalEstimatedHours
                  ? 'bg-red-500'
                  : 'bg-blue-500'
              }`}
              style={{ 
                width: `${Math.min(100, metrics.totalEstimatedHours > 0 
                  ? (metrics.totalActualHours / metrics.totalEstimatedHours) * 100 
                  : 0)}%` 
              }}
            />
          </div>
        </div>
      </div>

      {/* Project Performance */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Performance</h3>
        <div className="space-y-3">
          {visibleProjects.slice(0, 5).map((project) => (
            <div key={project.id} className="flex items-center gap-4">
              <div
                className="h-3 w-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: project.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {project.name}
                  </span>
                  <span className="text-sm text-gray-600">
                    {project.completion_percentage}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-blue-500 transition-all"
                    style={{ width: `${project.completion_percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ReportsDashboard;
